/**
 * TeacherPay (BON-247) — what each shift is worth, under the pay date it lands
 * on.
 *
 * The rule this page follows everywhere: never show a number we aren't sure of.
 * An unpriced shift shows an em-dash and says why; a group whose total is void
 * shows no total rather than a sum missing a shift. A confident wrong figure
 * about someone's pay is worse than an obvious gap.
 *
 * Hourly work is BON-249/250 and not here — this is workshop shift pay only.
 */
import { BucketError, BucketLoading, SectionCard } from '../regional/BucketStates';

import MyRatesCard from './components/MyRatesCard';
import PayDateGroup from './components/PayDateGroup';
import ShiftTable from './components/ShiftTable';
import { useTeacherPay } from './teacherApi';

export default function TeacherPay() {
  const q = useTeacherPay();

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  const { groups, unscheduled, calendarConfigured, salaried, myRates } = q.data.data;

  if (!calendarConfigured) {
    return (
      <div style={{ display: 'grid', gap: 10, maxWidth: 620 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Pay dates aren’t set up yet</div>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>
          Your shifts are recorded, but there’s no pay calendar to group them under yet. Once an
          admin adds pay periods, your pay will appear here grouped by pay date.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 820 }}>
      {salaried ? (
        <p role="note" style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>
          You’re salaried, so your workshop shifts aren’t paid per shift. They’re listed here so you
          can see where you worked.
        </p>
      ) : (
        myRates.length > 0 && <MyRatesCard rates={myRates} />
      )}

      {groups.length === 0 && unscheduled.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
          No shift pay yet. Shifts you work or are confirmed for show up here.
        </div>
      )}

      {groups.map((g) => (
        <PayDateGroup key={g.payDateIso} group={g} salaried={salaried} />
      ))}

      {unscheduled.length > 0 && (
        <SectionCard title={`Not on a pay date yet · ${unscheduled.length}`}>
          <p style={{ margin: '0 0 8px', fontSize: 11, color: 'var(--nb-subtle)' }}>
            These shifts fall outside the pay calendar, so they don’t have a pay date yet. Nothing
            is missing — the calendar just doesn’t reach them.
          </p>
          <ShiftTable shifts={unscheduled} salaried={salaried} />
        </SectionCard>
      )}
    </div>
  );
}
